import { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import commonMessages from "../../../common/messages";
import useDataState from "../../../hooks/useDataState";
import { useSupabaseClient } from "../../../common/supabaseClient";

export type LinkedPatient = Pick<
  DatabaseRow<"patients">,
  | "id"
  | "first_name"
  | "last_name"
  | "date_birth"
  | "legal_guardian_id"
  | "family_contact_id"
>;

const useCustomerLinkedPatients = (customerId?: string | null) => {
  const { formatMessage } = useIntl();
  const supabase = useSupabaseClient();

  const { dataState, updateDataState } = useDataState<LinkedPatient[]>();

  const fetchLinkedPatients = useCallback(async () => {
    try {
      if (!customerId) {
        updateDataState({ status: "success", data: [] });
        return;
      }

      updateDataState({ status: "loading", keepPreviousData: true });

      if (!supabase) return;

      const { data: customerData, error: customerError } = await supabase
        .from("customers")
        .select("id, email")
        .eq("id", customerId)
        .single();

      if (customerError) {
        throw new Error(formatMessage(commonMessages.defaultError));
      }

      const customerEmail = customerData.email;

      const { data, error } = await supabase
        .from("patients")
        .select(
          "id, first_name, last_name, date_birth, legal_guardian_id, family_contact_id",
        )
        .eq("email", customerEmail)
        .eq("is_archived", false);

      if (error) {
        throw new Error(formatMessage(commonMessages.defaultError));
      }

      updateDataState({ status: "success", data: data || [] });
    } catch (err) {
      updateDataState({
        status: "error",
        error: formatMessage(commonMessages.defaultError),
      });
    }
  }, [customerId, updateDataState, supabase, formatMessage]);

  useEffect(() => {
    fetchLinkedPatients();
  }, [fetchLinkedPatients]);

  return { ...dataState, refetch: fetchLinkedPatients };
};

export default useCustomerLinkedPatients;
